/**
 * ファイアウォールのルール評価（章 5 で使用）。
 *
 * 実機と同じく「order の小さいルールから順に評価し、最初にマッチしたものを適用」。
 * マッチするルールが 1 つも無い場合は暗黙の許可とする（学習用の簡略化）。
 * 暗黙 deny にすると、ルール未整備のデータで全通信が止まって学習の妨げになるため。
 */

import type { DeviceId, FirewallRule, NetworkWorld } from '../types/network';
import { ipInSubnetSpec } from './ip';

export type Protocol = 'tcp' | 'udp' | 'icmp';

export interface FirewallDecision {
  allowed: boolean;
  /** 遮断（または明示許可）を決めたルール。暗黙許可なら null */
  matchedRule: FirewallRule | null;
  /** 判断を下したファイアウォール機器 */
  deviceId: DeviceId | null;
}

const ALLOWED: FirewallDecision = { allowed: true, matchedRule: null, deviceId: null };

function ruleMatches(
  rule: FirewallRule,
  srcIp: string | null,
  dstIp: string | null,
  dstPort: number | null,
  protocol: Protocol,
): boolean {
  if (!ipInSubnetSpec(srcIp, rule.srcSubnet)) return false;
  if (!ipInSubnetSpec(dstIp, rule.dstSubnet)) return false;

  if (rule.protocol !== '*') {
    // ICMP（ping）は tcp/udp 限定のルールにはマッチしない
    if (protocol === 'icmp') return false;
    if (rule.protocol !== protocol) return false;
  }

  if (rule.dstPort !== '*') {
    if (dstPort === null) return false;
    if (rule.dstPort !== dstPort) return false;
  }
  return true;
}

/** 1 台のファイアウォールでの判定 */
export function evaluateFirewallDevice(
  world: NetworkWorld,
  firewallDeviceId: DeviceId,
  srcIp: string | null,
  dstIp: string | null,
  dstPort: number | null,
  protocol: Protocol,
): FirewallDecision {
  const device = world.devices[firewallDeviceId];
  if (!device || device.kind !== 'firewall' || !device.firewallRules) return ALLOWED;

  const rules = [...device.firewallRules].sort((a, b) => a.order - b.order);
  for (const rule of rules) {
    if (ruleMatches(rule, srcIp, dstIp, dstPort, protocol)) {
      return {
        allowed: rule.action === 'allow',
        matchedRule: rule,
        deviceId: firewallDeviceId,
      };
    }
  }
  return { allowed: true, matchedRule: null, deviceId: firewallDeviceId };
}

/** 経路上のすべてのファイアウォールを通した判定。1 台でも遮断すれば通らない */
export function evaluatePathFirewalls(
  world: NetworkWorld,
  path: DeviceId[],
  srcIp: string | null,
  dstIp: string | null,
  dstPort: number | null,
  protocol: Protocol,
): FirewallDecision {
  for (const deviceId of path) {
    const device = world.devices[deviceId];
    if (!device || device.kind !== 'firewall') continue;
    const decision = evaluateFirewallDevice(world, deviceId, srcIp, dstIp, dstPort, protocol);
    if (!decision.allowed) return decision;
  }
  return ALLOWED;
}
